import 'country-flag-icons/3x2/flags.css';
import { hasFlag } from 'country-flag-icons';

/*
Note: several libraries were considered.
- country-flag-icons (used in CCO): flag icons (not fully detailed) SVGs hardcoded into CSS with 200kB bundle size. With lazy-loading this is not a problem
- flag-icons: flags are fully detailed SVGs referenced as external file. Browser downloads them on demand, but the overall size is large – 5.5MB
- react-country-flag: negligible bundle size, as it uses emojis, but that's unreliable UX (OS dependent, may not be available at all).
- country-flags-svg: negligible bundle size, as it uses wikimedia SVGs that are lazy-loaded. The only concern is reliance on 3rd party CDN.

We want to avoid increasing overall bundle size, because this is used on a rather unimportant page that users will likely seldom visit.
The last one is the best choice, as it is reliable, and does not increase bundle size. It takes up a lot of place at our CDN but that's ok.
*/

export interface FlagIconProps {
    countryCode: string;
    height?: string;
    width?: string;
}

const defaultHeight = 16; // [px], matches CCO
const defaultWidth = 24; // [px], matches CCO

/**
 * Renders a flag icon for a given country code
 * Looks similar to consolidated checkout (CCO), but uses a different library that enables lazy-loading of the country flag icons
 *
 * @param countryCode two letter country code (ISO 3166-1 alpha-2)
 * @param height height in px, defaults to 16px
 * @param width width in px, defaults to 24px
 */
export const FlagIcon = ({ countryCode, height, width }: FlagIconProps) => {
    if (!hasFlag(countryCode)) {
        return null;
    }
    // the lib exposes flags as individual exports, so you can't reference them dynamically, but for that purpose it exposes class names, which we can infer
    // the `flag` class sets size and repeat, so all that's left is to override its default width & height
    const flagClass = `flag:${countryCode}`;

    return (
        <span
            className={flagClass}
            style={{
                height: height || defaultHeight,
                width: width || defaultWidth,
            }}
        />
    );
};
